import type { CSSObject } from "type/tss";
import { Theme } from "@mui/material/styles";
import * as env from "shared/env";


export const styles = (theme: Theme) =>
	({
		card: {
			backgroundColor: theme.palette.background.paper,
			borderTop: "1px solid #ebebeb",
			borderLeft: "1px solid #ebebeb",
			borderRight: "1px solid #ebebeb",
			borderBottom: "none",
			boxShadow: "none"
		},
		cardContent: {
			alignItems: "flex-start",
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-between",
			padding: 24,
			"&:last-child": {
				paddingBottom: 24
			}
		},
		leftSection: {
			display: "flex",
			gap: theme.spacing(2),
			alignItems: "flex-start",
			flex: 1,
			minWidth: 0,
		},
		checkbox: {
			height: 24,
			width: 24,
			padding: 0,
			flexShrink: 0,
		},
		contentSection: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(1),
			flex: 1,
			minWidth: 0,
			overflow: "hidden",
		},
		cardActionArea: {
			textOverflow: "ellipsis",
			overflow: "hidden",
			width: "100%",
			padding: 0,
			borderRadius: 0,
			"&:hover": {
				backgroundColor: "transparent"
			},
			"& .MuiCardActionArea-focusHighlight": {
				backgroundColor: "transparent"
			}
		},
		titleRow: {
			display: "flex",
			flexDirection: "row",
			alignItems: "baseline",
			flexWrap: "wrap"
		},
		title: {
			fontSize: 18,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
			fontFamily: theme.typography.fontFamily,
			fontWeight: 400,
		},
		leadLink: {
			fontSize: 18,
			lineHeight: "24px",
			fontFamily: theme.typography.fontFamily,
			fontWeight: 400,
			color: theme.palette.primary.main,
			textDecoration: "underline",
			"&:hover": {
				textDecoration: "underline"
			}
		},
		checkedLink: {
			textDecoration: "underline line-through"
		},
		forText: {
			fontSize: 18,
			lineHeight: "24px",
			fontFamily: theme.typography.fontFamily,
			fontWeight: 400,
			color: theme.palette.custom.textPrimary,
			margin: "0 4px"
		},
		description: {
			fontSize: 16,
			lineHeight: "24px",
			color: theme.palette.custom.textSecondary,
			fontFamily: theme.typography.fontFamily,
			fontWeight: 400,
			wordBreak: "break-word",
			overflowWrap: "break-word",
		},
		checkedText: {
			textDecoration: "line-through",
		},
		dateRow: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1)
		},
		dateIconOverdue: {
			width: 16,
			height: 16,
			flexShrink: 0,
			color: theme.palette.error.main
		},
		dateIconNormal: {
			width: 16,
			height: 16,
			flexShrink: 0,
			color: theme.palette.custom.textSecondary
		},
		date: {
			fontSize: 16,
			lineHeight: "24px",
			fontFamily: theme.typography.fontFamily,
			fontWeight: 400,
		},
		dateOverdue: {
			color: theme.palette.error.main,
		},
		dateNormal: {
			color: theme.palette.custom.textSecondary,
		},
		rightSection: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1.5),
			flexShrink: 0
		},
		agentRow: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(0.5),
			fontSize: 16,
			lineHeight: "24px",
			fontFamily: theme.typography.fontFamily,
			fontWeight: 400,
		},
		agentLabel: {
			color: theme.palette.custom.textSecondary,
		},
		agentName: {
			color: theme.palette.primary.main,
			textDecoration: "underline",
			cursor: "pointer",
			"&:hover": {
				textDecoration: "underline"
			}
		},
		typeChip: {
			height: 32,
			backgroundColor: theme.palette.primary.faded,
			borderRadius: 16,
			padding: theme.spacing(1, 2),
			gap: theme.spacing(1.25),
			display: "flex",
			alignItems: "center",
			"& .MuiChip-label": {
				fontSize: 14,
				lineHeight: "20px",
				fontFamily: theme.typography.fontFamily,
				fontWeight: 400,
				// MAX-3909 3f: pre-existing bug — `theme.palette.text` is the whole TypeText object, not a color
				// string; JSS emitted invalid CSS that browsers ignored (label inherits color). Cast preserves that
				// exact no-op runtime behavior under the stricter tss `satisfies CSSObject` typing. Flagged for fidelity.
				color: theme.palette.text as unknown as string,
				padding: 0
			},
			"& .MuiChip-icon": {
				margin: 0,
				width: 16,
				height: 16,
				color: theme.palette.primary.main
			}
		},
		typeChipMobile: {
			display: "none",
			[theme.breakpoints.down("xs")]: {
				display: "flex"
			}
		},
		typeChipDesktop: {
			display: "flex",
			[theme.breakpoints.down("xs")]: {
				display: "none"
			}
		},
		iconButton: {
			padding: 0,
			width: 24,
			height: 24,
			"& svg": {
				width: 24,
				height: 24
			}
		},
		flagIcon: {
			width: 24,
			height: 24,
		},
		moreIcon: {
			width: 24,
			height: 24,
		},
		flexRow: {
			display: "flex",
			flexDirection: "row"
		},
		priorityAndTrashContainer: {
			display: "flex",
			flexDirection: "row",
			margin: 10
		},
		agentLink: {
			fontSize: 15,
			color: theme.palette.secondary.main,
			whiteSpace: "nowrap",
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			maxWidth: "140px",
		},
	} satisfies Record<string, CSSObject>);
